import { Alert, Skeleton, Stack, Text } from "@mantine/core";
import { graphql } from "gql.tada";
import { useQuery } from "@apollo/client/react";
import { toIsoTimestamp } from "./validation.service";

const VALIDATION_ERROR_EVENTS_QUERY = graphql(`
  query ValidationErrorEvents($jobId: ID!) {
    stepRuleSetValidationEvents(
      jobId: $jobId
      kindFilter: [ERROR]
      page: { first: 100 }
    ) {
      nodes {
        id
        kind
        summary
        ruleId
        sequence
        timestamp
        effectType
        gameId
        groupId
        matchId
        roundId
        teamId
        stepId
        crossStepRuleId
      }
    }
  }
`);

interface ValidationErrorEventView {
  readonly id: string;
  readonly summary: string;
  readonly ruleId: string | null | undefined;
  readonly sequence: number;
  readonly timestamp: string;
  readonly effectType: string | null | undefined;
  readonly gameId: string | null | undefined;
  readonly groupId: string | null | undefined;
  readonly matchId: string | null | undefined;
  readonly roundId: string | null | undefined;
  readonly teamId: string | null | undefined;
  readonly stepId: string | null | undefined;
  readonly crossStepRuleId: string | null | undefined;
}

interface ValidationErrorCardProps {
  readonly event: ValidationErrorEventView;
}

function ValidationErrorCard(props: ValidationErrorCardProps) {
  const { event } = props;
  const contextLabel = buildContextLabel(event);

  return (
    <Alert color="red" title={`Error #${event.sequence}`}>
      <Stack gap="xs">
        {/* Mantine Text escapes children → safe against XSS from server-supplied summaries. */}
        <Text size="sm">{event.summary}</Text>
        {event.ruleId ? (
          <Text size="xs" c="dimmed">
            Rule: {event.ruleId}
          </Text>
        ) : null}
        {event.effectType ? (
          <Text size="xs" c="dimmed">
            Effect: {event.effectType}
          </Text>
        ) : null}
        {contextLabel ? (
          <Text size="xs" c="dimmed">
            Context: {contextLabel}
          </Text>
        ) : null}
        <Text size="xs" c="dimmed">
          {new Date(event.timestamp).toLocaleString()}
        </Text>
      </Stack>
    </Alert>
  );
}

function buildContextLabel(event: ValidationErrorEventView): string {
  const parts: string[] = [];
  if (event.gameId) parts.push(`game=${event.gameId}`);
  if (event.groupId) parts.push(`group=${event.groupId}`);
  if (event.roundId) parts.push(`round=${event.roundId}`);
  if (event.matchId) parts.push(`match=${event.matchId}`);
  if (event.teamId) parts.push(`team=${event.teamId}`);
  if (event.crossStepRuleId) parts.push(`cross-step=${event.crossStepRuleId}`);
  return parts.join(" · ");
}

interface ValidationErrorListProps {
  readonly jobId: string;
}

function ValidationErrorList(props: ValidationErrorListProps) {
  const { data, loading, error } = useQuery(VALIDATION_ERROR_EVENTS_QUERY, {
    variables: { jobId: props.jobId },
  });

  if (loading && !data) {
    return (
      <Stack gap="sm">
        <Skeleton height={16} width={120} />
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </Stack>
    );
  }

  // The Apollo onError link already surfaces a notification; fall back to a
  // quiet inline message here so the surrounding panel still renders.
  if (error) {
    return (
      <Text size="xs" c="red">
        Failed to load validation errors.
      </Text>
    );
  }

  const events: readonly ValidationErrorEventView[] = (
    data?.stepRuleSetValidationEvents?.nodes ?? []
  ).map<ValidationErrorEventView>((event) => ({
    id: event.id,
    summary: event.summary,
    ruleId: event.ruleId,
    sequence: event.sequence,
    timestamp: toIsoTimestamp(event.timestamp),
    effectType: event.effectType,
    gameId: event.gameId,
    groupId: event.groupId,
    matchId: event.matchId,
    roundId: event.roundId,
    teamId: event.teamId,
    stepId: event.stepId,
    crossStepRuleId: event.crossStepRuleId,
  }));

  if (events.length === 0) return null;

  return (
    <Stack gap="sm">
      <Text fw={600} c="red">
        Errors ({events.length})
      </Text>
      {events.map((event) => (
        <ValidationErrorCard key={event.id} event={event} />
      ))}
    </Stack>
  );
}

export {
  ValidationErrorCard,
  ValidationErrorList,
  VALIDATION_ERROR_EVENTS_QUERY,
};
export type { ValidationErrorEventView };
